import React, { useState } from 'react'
import { connect } from 'react-redux'
import { injectIntl, FormattedMessage } from 'react-intl'
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import {
  HighlightOff as DeleteIcon,
  CheckCircleOutline as CompleteIcon,
  Replay as ReopenIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  CloudDownload as DownloadIcon,
  RateReview as ReviewIcon,
  ArrowBackTwoTone as IconBack,
} from '@material-ui/icons'
import {
  deleteJob,
  updateJob,
  resetTasks,
  updateJobTask,
  fetchJobTaskList,
  fetchJobReportPdf,
} from 'reducers/myJobs/reducer'
import {
  COMPLETE_ITEM,
  STATUS_COMPLETED,
  REOPEN_ITEM,
  SEND_FOR_REVIEW,
  SEND_BACK_FROM_REVIEW,
  PENDING,
} from 'helpers/jobs'
import { DELETION_REASON_FIELDS } from 'routes/App/JobsAdmin/Jobs/JobWorkflowForm/constants'
import withPromise from 'helpers/withPromise'
import html2pdf from 'html2pdf.js'
import generateHTMLTemplate, {
  generateHTMLTemplateForWorkflow9733,
  generateHTMLTemplateForWorkflow9734,
} from 'helpers/generateHTMLTemplate'
import RoleHelper from 'helpers/role'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import DeletionReason from './Common/DeletionReasonField'
import messages from './translate'

const ACTION_TYPE_DELETE = 'delete'

const ExtraMenu = props => {
  const {
    intl: { formatMessage },
    job,
    allowedToDeleteJobs,
    allowedToDeleteJobSeries,
    allowedToReopenJobs,
    disabledBtn,
    roleLevel,
    onCloseMenu,
    activeJobId,
    reloadJobList,
    resetTasks,
    resetJobData,
    tasks,
    isJobUpdating,
    updateJob,
    deleteJob,
    fetchJobTaskList,
    fetchJobReportPdf,
    updateJobTask,
    jobTask,
    profileName,
  } = props

  const {
    title,
    series,
    needs_approval: needsApproval,
    needs_deletion_reason: needsDeletionReason,
    workflow,
    job_reports: jobReports,
  } = job

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [popupTitle, setPopupTitle] = useState('')
  const [popupMessage, setPopupMessage] = useState('')
  const [popupConfirm, setPopupConfirm] = useState({})
  const [actionType, setActionType] = useState('')
  const [currentActionKey, setCurrentActionKey] = useState(null)
  const [deletionReasonFieldValue, setDeletionReasonFieldValue] = useState(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const handleOpenConfirmDialog = (actionKey, params, _actionType = '') => {
    const { popupTitle, popupMessage, popupConfirm: confirmProps = {} } = params

    setIsDialogOpen(true)
    setActionType(_actionType)
    setCurrentActionKey(actionKey)
    setPopupTitle(popupTitle)
    setPopupMessage(popupMessage)
    setPopupConfirm(confirmProps)
  }

  const handleCloseConfirmDialog = () => {
    onCloseMenu()
    setIsDialogOpen(false)
    setActionType('')
    setCurrentActionKey(null)
  }

  const handleChangeDeletionReasonFieldValue = value => {
    setDeletionReasonFieldValue(value)
  }

  const handleDelete = seriesFlag => {
    const isDeleteSeries = job.series && seriesFlag
    withPromise(deleteJob, {
      id: job.id,
      series: isDeleteSeries,
      data:
        needsDeletionReason && deletionReasonFieldValue
          ? { deletion_reason: deletionReasonFieldValue }
          : undefined,
    }).then(res => {
      if (res && res.errors) return Promise.resolve(res.errors)

      reloadJobList()
      if (job.id === activeJobId) {
        resetTasks()
      }
    })

    handleCloseConfirmDialog()
  }

  const handleJobTaskUpdate = async (action, id) => {
    await withPromise(updateJobTask, {
      action,
      params: { id },
    }).then(res => {
      if (res && res.errors) return Promise.resolve(res.errors)

      return null
    })
  }

  const getFirstReport = () => {
    if (!jobReports || jobReports.length === 0) {
      return null
    }

    return jobReports[0]
  }

  const generatePDF = async () => {
    if (!job?.workflow) return

    try {
      await withPromise(fetchJobTaskList, {
        activeJobId: job.id,
        skipLoader: false,
      })

      const updatedJob = jobTask?.toJS?.()
      if (!updatedJob) throw new Error('Failed to fetch job data.')
      let htmlContent
      let fileName = `Job_${updatedJob.id}_Dispatch_Note.pdf`

      if (job.workflow === 9733) {
        htmlContent = await generateHTMLTemplateForWorkflow9733(updatedJob)
        fileName = `Job_${updatedJob.number}_Onsite_Checklist_Autopsies.pdf`
      } else if (job.workflow === 9734) {
        htmlContent = await generateHTMLTemplateForWorkflow9734(updatedJob)
      } else {
        htmlContent = await generateHTMLTemplate(updatedJob, profileName)
      }

      const element = document.createElement('div')
      element.innerHTML = htmlContent
      document.body.appendChild(element)

      const opt = {
        margin: 0.5,
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      }

      const pdfBlob = await html2pdf().set(opt).from(element).output('blob')

      document.body.removeChild(element)

      if (!(pdfBlob instanceof Blob)) {
        throw new TypeError('Generated PDF is not a Blob.')
      }

      return pdfBlob
    } catch (err) {
      console.error('Error generating PDF:', err)
      throw err
    }
  }

  // eslint-disable-next-line max-statements
  const handleDownloadPDF = async () => {
    const report = getFirstReport()
    setIsGeneratingPDF(true)

    try {
      if (report) {
        const pdfBlob = await withPromise(fetchJobReportPdf, {
          jobId: job.id,
          reportTemplateId: report.id,
        })
        const url = window.URL.createObjectURL(pdfBlob)
        const link = document.createElement('a')
        link.href = url
        const safeName = report.template_name.replace(/\s+/g, '_').toLowerCase()
        link.download = `job_${job.id}_${safeName}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        const paths = {
          10096: '/assets/reports/Report 1.pdf',
          10097: '/assets/reports/Report 2.pdf',
          11003: '/assets/reports/Demo_Ecto_PDF_Report_v6.pdf',
        }
        if ([10096, 10097, 11003].includes(workflow)) {
          const staticPath = paths[workflow]
          const res = await fetch(staticPath)
          if (!res.ok) throw new Error(res.statusText)
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `Job_${job.id}_${job.title}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        } else {
          const blob = await generatePDF()
          const url = URL.createObjectURL(blob)
          const filename =
            workflow === 9733
              ? `Job_${job.number}_Onsite_Checklist_Autopsies.pdf`
              : `Job_${job.id}_Dispatch_Note.pdf`
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }
      }
    } catch (err) {
      console.error('Download error:', err)
    } finally {
      setIsGeneratingPDF(false)
      onCloseMenu()
    }
  }

  const handlePrintPDF = async () => {
    const report = getFirstReport()
    setIsGeneratingPDF(true)

    try {
      if (report) {
        const pdfBlob = await withPromise(fetchJobReportPdf, {
          jobId: job.id,
          reportTemplateId: report.id,
        })
        const url = window.URL.createObjectURL(pdfBlob)
        const printWindow = window.open(url, '_blank')
        if (!printWindow) throw new Error('Cannot open print window')
        printWindow.focus()
        printWindow.onload = () => {
          printWindow.print()
          printWindow.onafterprint = () => {
            printWindow.close()
            window.URL.revokeObjectURL(url)
          }
        }
      } else {
        const paths = {
          10096: '/assets/reports/Report 1.pdf',
          10097: '/assets/reports/Report 2.pdf',
          11003: '/assets/reports/Demo_Ecto_PDF_Report_v6.pdf',
        }

        if ([10096, 10097, 11003].includes(workflow)) {
          const staticPath = paths[workflow]
          const res = await fetch(staticPath)
          if (!res.ok) throw new Error(res.statusText)
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          const printWindow = window.open(url, '_blank')
          if (!printWindow) throw new Error('Cannot open print window')
          printWindow.focus()
          printWindow.onload = () => {
            printWindow.print()
            printWindow.onafterprint = () => {
              printWindow.close()
              window.URL.revokeObjectURL(url)
            }
          }
        } else {
          const blob = await generatePDF()
          const url = URL.createObjectURL(blob)
          const printWindow = window.open(url, '_blank')
          if (!printWindow) throw new Error('Cannot open print window')
          printWindow.focus()
          printWindow.onload = () => {
            printWindow.print()
            printWindow.onafterprint = () => {
              printWindow.close()
              window.URL.revokeObjectURL(url)
            }
          }
        }
      }
    } catch (err) {
      console.error('Print error:', err)
    } finally {
      setIsGeneratingPDF(false)
      onCloseMenu()
    }
  }

  const handleUpdate = async action => {
    if (isJobUpdating) return

    if (
      tasks.length === 1 &&
      (action === COMPLETE_ITEM || action === SEND_FOR_REVIEW) &&
      !tasks[0].optional &&
      tasks[0].state !== STATUS_COMPLETED
    ) {
      await handleJobTaskUpdate(COMPLETE_ITEM, tasks[0].id)
    }

    withPromise(updateJob, {
      action,
      params: { id: job.id },
      title: job.title,
    }).then(res => {
      if (res && res.errors) return Promise.resolve(res.errors)

      if (tasks.length === 1 && !tasks[0].optional && action === REOPEN_ITEM) {
        handleJobTaskUpdate(action, tasks[0].id)
      }

      reloadJobList()
      if (job.id === activeJobId) {
        resetJobData()
        resetTasks()
      }
    })

    handleCloseConfirmDialog()
  }

  // Define your action map (without the callback) for dialog texts only.
  const actionMap = {
    reopen: {
      popupTitle: formatMessage(messages.reopenJob),
      popupConfirm: {
        customText: formatMessage(messages.reopen),
        color: 'scales-primary',
      },
      popupMessage: (
        <FormattedMessage
          {...messages.confirmJobMessage}
          values={{
            action: formatMessage(messages.reopen).toLowerCase(),
            entry: <strong>{title}</strong>,
          }}
        />
      ),
    },
    complete: {
      popupTitle: formatMessage(messages.completeJob),
      popupConfirm: {
        customText: formatMessage(messages.complete),
        color: 'scales-primary',
      },
      popupMessage: (
        <FormattedMessage
          {...messages.confirmJobMessage}
          values={{
            action: formatMessage(messages.complete).toLowerCase(),
            entry: <strong>{title}</strong>,
          }}
        />
      ),
    },
    sendForReview: {
      popupTitle: formatMessage(messages.sendForReview),
      popupConfirm: {
        customText: formatMessage(messages.sendForReview),
        color: 'scales-primary',
      },
      popupMessage: (
        <FormattedMessage
          {...messages.confirmJobMessage}
          values={{
            action: formatMessage(messages.sendForReview).toLowerCase(),
            entry: <strong>{title}</strong>,
          }}
        />
      ),
    },
    sendBackFromReview: {
      popupTitle: formatMessage(messages.sendBackFromReview),
      popupConfirm: {
        customText: formatMessage(messages.sendBackFromReview),
        color: 'scales-primary',
      },
      popupMessage: (
        <FormattedMessage
          {...messages.confirmJobMessage}
          values={{
            action: formatMessage(messages.sendBackFromReview).toLowerCase(),
            entry: <strong>{title}</strong>,
          }}
        />
      ),
    },
    delete: {
      popupTitle: formatMessage(messages.deleteJob),
      popupConfirm: {
        customText: formatMessage(messages.delete),
        color: 'scales-primary',
      },
      popupMessage: (
        <>
          <FormattedMessage
            {...messages.confirmJobMessage}
            values={{
              action: formatMessage(messages.delete).toLowerCase(),
              entry: <strong>{title}</strong>,
            }}
          />
          {needsDeletionReason && (
            <DeletionReason
              onChangeDeletionReasonFieldValue={
                handleChangeDeletionReasonFieldValue
              }
              deletionReasons={job.deletion_reasons}
              deletionReasonType={job.deletion_reason_type}
            />
          )}
        </>
      ),
    },
    deleteSeries: {
      popupTitle: formatMessage(messages.deleteJobSeries),
      popupConfirm: {
        customText: formatMessage(messages.delete),
        color: 'scales-primary',
      },
      popupMessage: (
        <>
          <FormattedMessage
            {...messages.confirmJobSeriesMessage}
            values={{
              title: <strong>{title}</strong>,
              amount: (
                <strong>{series && series.total_not_completed_jobs}</strong>
              ),
            }}
          />
          {needsDeletionReason && (
            <DeletionReason
              onChangeDeletionReasonFieldValue={
                handleChangeDeletionReasonFieldValue
              }
              deletionReasons={job.deletion_reasons}
              deletionReasonType={job.deletion_reason_type}
            />
          )}
        </>
      ),
    },
  }

  // Handler that always uses the current state when confirming
  const handleConfirmAction = () => {
    switch (currentActionKey) {
      case 'delete':
        handleDelete()
        break
      case 'deleteSeries':
        handleDelete('series')
        break
      case 'reopen':
        handleUpdate(REOPEN_ITEM)
        break
      case 'complete':
        handleUpdate(COMPLETE_ITEM)
        break
      case 'sendForReview':
        handleUpdate(SEND_FOR_REVIEW)
        break
      case 'sendBackFromReview':
        handleUpdate(SEND_BACK_FROM_REVIEW)
        break
      default:
        break
    }
  }

  const showCompleteBtn =
    job.state !== STATUS_COMPLETED &&
    (job.state !== PENDING || roleLevel >= RoleHelper.ROLE_ADMIN_LEVEL) &&
    ((tasks.length === 1 && !disabledBtn) ||
      tasks.every(item => item.optional || item.state === STATUS_COMPLETED))

  const isPdfReport =
    (workflow && [9504, 9733, 9734, 10096, 10097, 11003].includes(workflow)) ||
    jobReports?.length > 0

  return (
    <>
      <List disablePadding className="extra-actions">
        {isPdfReport && (
          <>
            <ListItem
              button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}>
              <ListItemIcon>
                <DownloadIcon />
              </ListItemIcon>
              <ListItemText>{formatMessage(messages.downloadPdf)}</ListItemText>
            </ListItem>
            <ListItem
              button
              onClick={handlePrintPDF}
              disabled={isGeneratingPDF}>
              <ListItemIcon>
                <PrintIcon />
              </ListItemIcon>
              <ListItemText>{formatMessage(messages.printPdf)}</ListItemText>
            </ListItem>
          </>
        )}

        {job.state === STATUS_COMPLETED && (
          <ListItem
            button
            disabled={!allowedToReopenJobs}
            data-action={REOPEN_ITEM}
            onClick={() => handleOpenConfirmDialog('reopen', actionMap.reopen)}>
            <ListItemIcon>
              <ReopenIcon />
            </ListItemIcon>
            <ListItemText>{formatMessage(messages.reopen)}</ListItemText>
          </ListItem>
        )}

        {showCompleteBtn &&
          (needsApproval && job.state !== PENDING ? (
            <ListItem
              button
              data-action={SEND_FOR_REVIEW}
              onClick={() =>
                handleOpenConfirmDialog(
                  'sendForReview',
                  actionMap.sendForReview,
                )
              }>
              <ListItemIcon>
                <ReviewIcon />
              </ListItemIcon>
              <ListItemText>
                {formatMessage(messages.sendForReview)}
              </ListItemText>
            </ListItem>
          ) : (
            <ListItem
              button
              data-action={COMPLETE_ITEM}
              onClick={() =>
                handleOpenConfirmDialog('complete', actionMap.complete)
              }>
              <ListItemIcon>
                <CompleteIcon />
              </ListItemIcon>
              <ListItemText>{formatMessage(messages.complete)}</ListItemText>
            </ListItem>
          ))}

        {job.state === PENDING && roleLevel >= RoleHelper.ROLE_ADMIN_LEVEL && (
          <ListItem
            button
            data-action={SEND_BACK_FROM_REVIEW}
            onClick={() =>
              handleOpenConfirmDialog(
                'sendBackFromReview',
                actionMap.sendBackFromReview,
              )
            }>
            <ListItemIcon>
              <IconBack />
            </ListItemIcon>
            <ListItemText>
              {formatMessage(messages.sendBackFromReview)}
            </ListItemText>
          </ListItem>
        )}

        {allowedToDeleteJobs && (
          <ListItem
            button
            onClick={() =>
              handleOpenConfirmDialog(
                'delete',
                actionMap.delete,
                ACTION_TYPE_DELETE,
              )
            }>
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText>{formatMessage(messages.deleteJob)}</ListItemText>
          </ListItem>
        )}

        {series && allowedToDeleteJobSeries && (
          <ListItem
            button
            onClick={() =>
              handleOpenConfirmDialog(
                'deleteSeries',
                actionMap.deleteSeries,
                ACTION_TYPE_DELETE,
              )
            }>
            <ListItemIcon className="job-delete-series-icon">
              <ClearIcon />
            </ListItemIcon>
            <ListItemText>
              {formatMessage(messages.deleteJobSeries)}
            </ListItemText>
          </ListItem>
        )}
      </List>

      <ConfirmDialog
        open={isDialogOpen}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        isSubmitDisabled={
          actionType === ACTION_TYPE_DELETE &&
          needsDeletionReason &&
          ((job.deletion_reason_type === DELETION_REASON_FIELDS.LOOKUP &&
            !deletionReasonFieldValue?.lookup) ||
            (job.deletion_reason_type === DELETION_REASON_FIELDS.TEXTAREA &&
              !deletionReasonFieldValue?.text) ||
            (job.deletion_reason_type ===
              DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA &&
              (!deletionReasonFieldValue?.lookup ||
                !deletionReasonFieldValue?.text)))
        }
        onClose={handleCloseConfirmDialog}
        onConfirmAction={handleConfirmAction}
        className="dialog-primary-custom"
        title={popupTitle}
        confirm={popupConfirm}
        message={popupMessage}
      />
    </>
  )
}

export default connect(
  state => ({
    allowedToDeleteJobs: state.getIn([
      'profile',
      'profile',
      'grantedDeleteJobs',
    ]),
    allowedToDeleteJobSeries: state.getIn([
      'profile',
      'profile',
      'grantedDeleteJobSeries',
    ]),
    allowedToReopenJobs: state.getIn([
      'profile',
      'profile',
      'grantedReopenJobs',
    ]),
    jobTask: state.getIn(['myJobs', 'jobTask']),
    roleLevel: state.getIn(['profile', 'roleLevel']),
    profileName: state.getIn(['profile', 'profile', 'name']),
    isJobUpdating: state.getIn(['myJobs', 'isJobUpdating']),
  }),
  {
    updateJob,
    deleteJob,
    resetTasks,
    fetchJobTaskList,
    fetchJobReportPdf,
    updateJobTask,
  },
)(injectIntl(ExtraMenu))
